import { ChangeDetectionStrategy, Component, inject, input, model, OnInit, signal } from "@angular/core";
import { BiTranslateService } from "@globals/bi-translate";
import { ProgressSpinnerModule } from "primeng/progressspinner";

/**
 * Spinner component - for showing a spinner! See documentation of input properties for the awesome features!
 */
@Component({
  selector: "bi-spinner",
  templateUrl: "./bi-spinner.component.html",
  styleUrls: ["./bi-spinner.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "[class.fixed-pos]": "fixedPosition()",
    "[class.has-background]": "hasBackground()",
    "[class.on-top]": "onTopOfContainer()",
    "[class.center]": "centerInParent()",
    "[style.display]": "display()"
  },
  imports: [ProgressSpinnerModule]
})
export class BiSpinnerComponent implements OnInit {
  /**
   * Size of spinner. Default is medium
   */
  spinnerSize = input<"small" | "medium" | "large">("medium");

  /**
   * Set true if you want spinner to be displayed "inline-block" - for ex. when you want some text next to it. Default
   * is false meaning it will be displyed "block" (e.g. on its own line)
   */
  inline = input<boolean>(false);

  /**
   * Add a Spinner text to be shown
   */
  message = input<string>();

  /**
   * Set true if you want the opacity of the spinner background to be more visible
   */
  highOpacity = input<boolean>(false);

  /**
   * Will make the spinner be positioned fixed so user can always see it. Default true. On smaller pages (small height),
   * set this false but for best result
   */
  fixedPosition = input<boolean>(true);

  /**
   * Wether a background is shown. This is a greyed-out background with a bit transparency
   */
  hasBackground = input<boolean>(false);

  /**
   * Value for the ARIA label attribute. Default is translated value of translation key "accessibility.Working".
   */
  ariaLabel = model<string>(undefined);

  /**
   * Set this to true if you want the spinner to be displayed on top of parent container content.
   * REQUIREMENT FOR USE: parent container element MUST have css "position: relative" and display property set!
   */
  onTopOfContainer = model<boolean>(false);

  /**
   * Set this to true if you want the spinner to be displayed in the center of parent container content.
   * Notice: if you set this to true, the input property "onTopOfContainer" will automatically be true also, if not already set.
   * REQUIREMENT FOR USE: parent container element MUST have css "position: relative" and display property set!
   */
  centerInParent = input<boolean>(false);

  public display = signal("block");

  private readonly translator = inject(BiTranslateService);

  public ngOnInit() {
    if (this.centerInParent()) this.onTopOfContainer.set(true);

    if (this.inline()) this.display.set("inline-block");

    if (!this.ariaLabel()) this.ariaLabel.set(this.translator.instant("accessibility.Working"));
  }
}
